import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { FLAGSHIP_ORG, resolveModuleEnabled } from '@/lib/moduleAccess';
import useOrg from '@/contexts/OrgContext';
import { useAuth } from '@/contexts/SupabaseAuthContext';

// ── The org's active parts, shared ───────────────────────────────────────────
// Reads the SECURITY DEFINER projection public.active_module_keys(org) (the
// crew app cannot read admin-only business_module_entitlements directly) and
// follows it over Realtime, so a purchase or a cancellation flips every gate
// without a reload.
//
// One store per org, however many gates ask: the sidebar, the bottom bar, a
// dozen ModuleGates and the office menu share one RPC and one channel instead
// of each opening their own. The store lives while anything subscribes to it
// and keeps its last answer for the next subscriber.

const stores = new Map();

function getStore(orgKey) {
  if (stores.has(orgKey)) return stores.get(orgKey);
  const store = {
    keys: null, loaded: false, error: false,
    subs: new Set(), channel: null, started: false,
    snapshot() { return { keys: this.keys, loaded: this.loaded, error: this.error }; },
    notify() { for (const fn of this.subs) fn(); },
    async load() {
      const { data, error } = await supabase.rpc('active_module_keys', { p_org: orgKey });
      if (!this.started) return;
      if (error) {
        console.error('[module-access]', error.message);
        this.error = true;
      } else {
        this.keys = Array.isArray(data) ? data : [];
        this.error = false;
      }
      this.loaded = true;
      this.notify();
    },
    start() {
      if (this.started) return;
      this.started = true;
      this.load();
      this.channel = supabase
        .channel(`module-access-${orgKey}`)
        .on('postgres_changes',
          { event: '*', schema: 'public', table: 'business_module_entitlements', filter: `org_key=eq.${orgKey}` },
          () => { this.load(); })
        .subscribe();
    },
    stop() {
      this.started = false;
      if (this.channel) supabase.removeChannel(this.channel);
      this.channel = null;
    },
    subscribe(fn) {
      this.subs.add(fn);
      this.start();
      return () => {
        this.subs.delete(fn);
        if (this.subs.size === 0) this.stop();
      };
    },
  };
  stores.set(orgKey, store);
  return store;
}

const NOBODY = { keys: null, loaded: true, error: false };

/**
 * The org's active part keys: { keys, loaded, error }. `keys` is null until
 * the projection answers (or while signed out: active_module_keys is REVOKEd
 * from anon, so asking on behalf of nobody is a guaranteed 401 — the answer
 * is the fail-safe either way).
 */
export function useActiveModuleKeys(orgKeyOverride) {
  const { orgKey: sessionOrgKey } = useOrg();
  const { user } = useAuth();
  const orgKey = orgKeyOverride ?? sessionOrgKey ?? FLAGSHIP_ORG;
  // Keyed on the user's id, not the user object: a refreshed session is the
  // same person, and must not re-subscribe (or, with an unstable object,
  // loop).
  const userId = user?.id ?? null;
  const [snap, setSnap] = useState(() => (userId ? getStore(orgKey).snapshot() : NOBODY));

  useEffect(() => {
    if (!userId) { setSnap(NOBODY); return undefined; }
    const store = getStore(orgKey);
    const unsub = store.subscribe(() => setSnap(store.snapshot()));
    setSnap(store.snapshot());
    return unsub;
  }, [orgKey, userId]);

  return snap;
}

// ── useModuleEnabled ─────────────────────────────────────────────────────────
// Is `moduleKey` switched on for this org? Fail SAFE through
// resolveModuleEnabled() in lib/moduleAccess.js: while loading, or if the RPC
// errors, the flagship org defaults ENABLED (never hide a working feature
// over a hiccup) and a second tenant defaults disabled. An explicit org still
// wins, so an admin surface can inspect another org deliberately.
//
// Returns { enabled, loaded }.
export default function useModuleEnabled(moduleKey, orgKeyOverride) {
  const { orgKey: sessionOrgKey } = useOrg();
  const orgKey = orgKeyOverride ?? sessionOrgKey ?? FLAGSHIP_ORG;
  const { keys, loaded, error } = useActiveModuleKeys(orgKeyOverride);
  const enabled = resolveModuleEnabled({ keys, moduleKey, org: orgKey, loaded, error });
  return { enabled, loaded };
}
